import { getCurrentLanguage } from '@eureka/language-tool';
import { addTrialService } from '@eureka/services/src/add-trial-service';
import { CommonApis } from '@eureka/services/src/apis/common.api';
import { isAnonymousError } from '@eureka/services/src/exception';
import { accountDeferred, prepareTokenDeferred, setPrepareTokenDeferred } from '@eureka/utils/account';
import { HeaderService, SLD, Site } from '@patsnap-utils/biz';
import { handleLogout } from '@patsnap-utils/passport';
import psHttp, { generateExceptionRedirector, psAxios } from '@patsnap/common-ps-http';
import { createUniformSetting } from '@patsnap/uniform-setting';
import type { T_Locale, T_Region } from '@patsnap/uniform-setting/dist/locale/types';
import type { AxiosInstance } from 'axios';
import _ from 'lodash';
import browser from './browser';
import setWebsiteFont from './configure-website-font';
import { customProductsLinkInUserCenter, type CustomLinksOptions } from './custom-links';
import { type IProjectPreset, _passportDeferred, passportInterceptManager, prepareTokenBase, updateTokenBaseRequestHeaders } from './enable-token-base';
import { removeEurekaLoginCookie, setEurekaLoginCookie } from './set-eureka-login-cookie';
import { clientDeferred } from '@eureka/utils/client';

const supports: T_Locale[] = ['cn', 'en', 'jp', 'tw']

export interface IStartupOption {
    headerService?: HeaderService
    projectName: string
    preset?: IProjectPreset
    pageInit: () => Promise<unknown>
    /**
     * 禁用自动赋权
     */
    disableTrial?: boolean
    /**
     * 禁止加载客服
     */
    disableCustomerService?: boolean,
    /**
     * 自定义链接选项
     */
    customLinksOptions?: CustomLinksOptions
}

const lang = getCurrentLanguage()

const { includes, filter, get } = _

export async function startup(startupOption: IStartupOption): Promise<unknown[]> {
    browser()
    const { isFeiShu } = await clientDeferred.promise
    if (isFeiShu) {
        startupOption.disableCustomerService = true
    }
    const promise = startupOption.preset
        ? prepareTokenBase(startupOption.preset).then((p) => {
            setEurekaLoginCookie();
            setPrepareTokenDeferred(p)
            /* send auth ready signal */
            return p;
        }).catch(e => {
            if (isAnonymousError(e)) {
                // 只有在startup的preset.preRedirectLogin中手动throw new AnonymousError才会触发
                passportInterceptManager.eject();
                accountDeferred.reject(e);
                prepareTokenDeferred.reject(e);
                _passportDeferred.reject(e);
            }
        })
        : Promise.resolve()
    // @ts-ignore
    return promise.then((p) => {
        const headerService = startupOption.headerService || new HeaderService()
        headerService.logoHref = new Site({ sld: SLD.EUREKA, pathname: 'home' }).href

        return Promise.all([
            startupOption.pageInit().catch(e => {
                if (!addTrialService.ENABLE_AUTO_ADD_TRIAL) return Promise.reject(e)
            }),
            headerService.sync({ http: psHttp, uri: process.env.VUE_APP_ACCOUNT_SERVICE_URI as string }).finally(
                () => {
                    customProductsLinkInUserCenter(headerService, startupOption.customLinksOptions)
                    headerService.visibleProducts = !isFeiShu
                    headerService.account && accountDeferred.resolve(headerService.account);
                }
            ).then(() => {
                if (startupOption.disableTrial) {
                    return;
                }
                return addTrialService.addTrial(headerService, p)
            }),
            (() => {
                const setting = createUniformSetting({
                    verbose: true,
                    axiosInstance: psAxios as AxiosInstance as any,
                    loggedIn: true,
                    baseAccountUrl: process.env.VUE_APP_ACCOUNT_SERVICE_URI,
                    locale: {
                        supports,
                    },
                    region: process.env.VUE_APP_DEPLOY_ENV as T_Region,
                })

                // @ts-ignore
                return setting.locale.getLocale().then((data) => {
                    const locale = data.user || data.default
                    setWebsiteFont(locale)
                    updateTokenBaseRequestHeaders({ 'X-Site-Lang': locale.toUpperCase() })
                    return { locale, setting }
                })
            })()
        ]).then((data) => {
            const [pageData, , { locale, setting }] = data

            /* 如果 cookies 中存储的语言和持久化的语言不匹配，则刷新页面 */
            if (lang !== locale) {
                window.location.reload()
                return
            }

            /* WhiteLabel 时，隐藏 PatSnap Logo */
            if (headerService.whiteLabel) {
                headerService.logoVisible = false
            }

            const { locales } = headerService
            headerService.locales = filter(locales, locale => includes(supports, locale.key)).map(({ ...rest }) => {
                return {
                    ...rest,
                    active: rest.key === locale,
                }
            })

            /* 注册切换语言函数 */
            // eslint-disable-next-line @typescript-eslint/ban-types
            headerService.beforeLocaleChange = function beforeLocaleChange(localeKey: string, done: Function) {
                setting.locale.setLocale(localeKey as T_Locale).then(() => {
                    window.location.reload()
                    done()
                })
            }

            /* 注册登出回调函数 */
            // eslint-disable-next-line @typescript-eslint/ban-types
            headerService.userCenterService.logoutHandler = async function logout(done: Function, logoutHref: string, option: unknown) {
                handleLogout().then((data: unknown) => {
                    removeEurekaLoginCookie();
                    setTimeout(() => {
                        const { createLogoutHref } = option as { createLogoutHref: (appendRedirect?: boolean, query?: object) => string }
                        const url = createLogoutHref(true, { anonymousLogin: false })
                        const href = get(data, 'data.redirect_url', url);
                        window.location.href = href;
                    }, 1000)
                });
            }

            /** header头切换租户成功后页面跳转 */
            headerService.switchTenantHandler = async(tenantId, done) => {
                return done().then(() => {
                    window.location.href = new Site({ sld: SLD.EUREKA, pathname: 'home' }).href
                })
            }

            accountDeferred.promise.then(account => {
                if (account?.isLogin) {
                    import(/* webpackChunkName: "defer" */'./defer').then(({ defer }) => {
                        defer(startupOption, account, locale)
                    })
                }
            })

            // 用户是否需要onBoarding
            const excludePath = ['/onboarding', '/ai-ls/.*', '/ls/.*']
            if (!excludePath.some(p => new RegExp(p).test(window.location.pathname)) && !isFeiShu) {
                CommonApis.getUserIsNeedOnBoarding().then(res => {
                    if (res.required) {
                        const { industry = '', role = '' } = res;
                        const redirect = window.location.href.replace(window.location.origin, '');
                        window.location.href = `/onboarding?industry=${encodeURIComponent(industry)}&role=${encodeURIComponent(role)}&redirect=${encodeURIComponent(redirect)}`;
                    }
                })
            }

            return [headerService, locale, pageData, setting]
        })
            .catch((error: Error) => {
                if (!addTrialService.ENABLE_AUTO_ADD_TRIAL) {
                    generateExceptionRedirector(403, () => {
                        window.location.href = new Site({ sld: SLD.EUREKA, pathname: '403/' }).href
                    })(error);
                }
            });
    })
}
